import { getField, updateField } from 'vuex-map-fields';
import map from 'lodash/map';
import findIndex from 'lodash/findIndex';
import i18n from '@/plugins/i18n';
import simulationRepo from '@/repositories/simulationRepository';
import { multiply, bignumber } from 'mathjs';


function indexOfSalaryType(salaryTypes, salaryType) {
  return findIndex(salaryTypes, s => s === salaryType) + 1;
}
function indexOfCalculationType(calculationTypes, calculationType) {
  return findIndex(calculationTypes, s => s === calculationType) + 1;
}

const getDefaultState = () => ({
  medicalDocument: {
    nl: 'https://mybaloise.baloise.be/dam/jcr:39446d83-effa-416a-80b0-2dad0eee185e',
    fr: 'https://mybaloise.baloise.be/dam/jcr:4341ee20-d3d8-4ec0-bc85-b7bb47a6ff64',
    en: 'https://mybaloise.baloise.be/dam/jcr:39446d83-effa-416a-80b0-2dad0eee185e',
  },
  beneficiaryDocument: {
    nl: 'https://mybaloise.baloise.be/dam/jcr:97d2aef5-1464-45b0-a05a-8c34e689977a',
    fr: 'https://mybaloise.baloise.be/dam/jcr:f26f21df-82a5-4ca0-af6f-7afc4230fdd8',
    en: 'https://mybaloise.baloise.be/dam/jcr:97d2aef5-1464-45b0-a05a-8c34e689977a',
  },
  simulation: null,
  calculationState: '',
  simulationId: null,
  calculationSaved: false,
  contractIdSaved: null,
  acceptedResult: '',
  comment: '',
  acceptedPremiumPension: 0,
  acceptedBackservice: 0,
  choicesDisabled: false,
  calculationTypes: ['maximumPremium',
    'maximumPremiumMaximumBackservice',
    'maximumBackserviceMaintainPremium'],
  explanation: '',
  professionId: null,
  questions: null,
  answers: [],
  loading: false,
  commissionPremium: 0,
  calculationRequest: {
    calculationTypeKeys: ['maximumPremium',
      'maximumPremiumMaximumBackservice',
      'maximumBackserviceMaintainPremium'],
    dateClosingFiscalYear: '',
    maritalState: null,
    salaries: [{
      key: 'totalGrossRemuneration',
      value: 0,
    }, {
      key: 'benefitInKind',
      value: 0,
    }, {
      key: 'socialSecurityContributions',
      value: 0,
    }, {
      key: 'VAPZContributions',
      value: 0,
    }],
    commission: 0,
  },
});

const state = getDefaultState();

const getters = {
  getField,
  getSimulation(st) {
    return st.simulation;
  },
  getCalculationRequest(st) {
    return st.calculationRequest;
  },
  getAnswers(st) {
    return st.answers;
  },
  getSalaryTypes() {
    return Object.keys(i18n.messages.en.lookUpValues.salaryTypes);
  },
  getCalculationTypes(st) {
    return st.calculationTypes;
  },
  getProfessionKey(st) {
    return Object.keys(i18n.messages.en.lookUpValues.professions)[st.professionId - 1];
  },
  getProfessions() {
    return Object.keys(i18n.messages.en.lookUpValues.professions);
  },
  getCalculationState(st) {
    return st.calculationState;
  },
  getSimulationId(st) {
    return st.simulationId;
  },
  getQuestions(st) {
    return st.questions;
  },
  getLoading(st) {
    return st.loading;
  },
};

const mutations = {
  updateField,
  setSimulation(st, value) {
    st.simulation = value;
  },
  setCalculationResult(st, value) {
    st.calculationResult = value;
  },
  setAcceptedResult(st, value) {
    st.acceptedResult = value;
  },
  setCalculationState(st, value) {
    st.calculationState = value;
  },
  setChoicesDisabled(st, value) {
    st.choicesDisabled = value;
  },
  setSimulationId(st, value) {
    st.simulationId = value;
  },
  setQuestions(st, value) {
    st.questions = value;
  },
  setProfessionId(st, value) {
    st.professionId = value;
  },
  setLoading(st, value) {
    st.loading = value;
  },
  setCalculationSaved(st, value) {
    st.calculationSaved = value;
  },
  setAcceptedPremiumPension(st, value) {
    st.acceptedPremiumPension = value;
  },
  setAcceptedBackservice(st, value) {
    st.acceptedBackservice = value;
  },
  setCalculationRequest(st, value) {
    st.calculationRequest = value;
  },
  setDefaultValues(st, value) {
    st.calculationRequest.maritalState = value.maritalState;
    st.calculationRequest.commission = value.commissionBackService * 100;
    st.commissionPremium = multiply(bignumber(value.commissionPremium), 100);
  },
  setAnswers(st, value) {
    st.answers = [];
    value.forEach(q => st.answers.push({ questionId: q.questionId, answer: undefined }));
  },
  setContractIdSaved(st, value) {
    st.contractIdSaved = value;
  },
  setDefaultState(st, value) {
    Object.assign(st, getDefaultState());
    st.calculationRequest.maritalState = value.maritalState;
    st.calculationRequest.commission = (value.commissionBackService) * 100;
    st.commissionPremium = multiply(bignumber(value.commissionPremium), 100);
  },
};
const actions = {
  async createSimulation({
    // eslint-disable-next-line no-shadow
    commit, rootState, state, getters, dispatch,
  }) {
    if (rootState.detail.primaryContract) {
      commit('setLoading', true);
      commit('setCalculationState', '');
      let language = '';
      switch (i18n.locale) {
        case ('nl'): language = 'nl-BE'; break;
        case ('en'): language = 'en-US'; break;
        case ('fr'): language = 'fr-FR'; break;
        default: language = 'nl-BE'; break;
      }
      const simulation = {
        // eslint-disable-next-line no-return-await
        calculationTypeKeys: map(state.calculationRequest.calculationTypeKeys, c => indexOfCalculationType(getters.getCalculationTypes, c)),
        dateClosingFiscalYear: state.calculationRequest.dateClosingFiscalYear,
        maritalStateKey: state.calculationRequest.maritalState.id,
        salaries: map(state.calculationRequest.salaries, s => ({ salaryTypeId: indexOfSalaryType(getters.getSalaryTypes, s.key), salaryValue: s.value ? s.value : 0 })),
        commission: state.calculationRequest.commission,
        language,
      };
      const response = await simulationRepo
        .createSimulation(simulation, rootState.detail.primaryContract);
      if (response && response.status === 200 && !response.data.errors) {
        commit('setCalculationState', response.data.data.createSimulation.state);
        if (state.calculationState === 'moreInfoRequired') {
          commit('setQuestions', response.data.data.createSimulation.questions);
          commit('setProfessionId', response.data.data.createSimulation.professionId);
          commit('setAnswers', response.data.data.createSimulation.questions);
        }
        commit('setSimulationId', response.data.data.createSimulation.simulationId);
      } else {
        dispatch('general/setError', true, { root: true });
      }
    }
    commit('setLoading', false);
  },
  async getQuestions({ commit, dispatch }, professionKey) {
    if (state.simulationId) {
      const response = await simulationRepo
        .getQuestions(professionKey);

      if (response && response.status === 200 && !response.data.errors) {
        commit('setQuestions', response.data.data.questions);
      } else {
        dispatch('general/setError', true, { root: true });
      }
    }
  },
  // eslint-disable-next-line no-shadow
  async getSimulationDetails({ commit, dispatch }, simulationId) {
    if (simulationId) {
      commit('setLoading', true);
      let language = '';
      switch (i18n.locale) {
        case ('nl'): language = 'nl-BE'; break;
        case ('en'): language = 'en-US'; break;
        case ('fr'): language = 'fr-FR'; break;
        default: language = 'nl-BE'; break;
      }
      const response = await simulationRepo
        .getSimulationDetails(simulationId, language);
      if (response && response.status === 200 && !response.data.errors && response.data.data.simulation) {
        response.data.data.simulation.calculationDetails.commissionBackService = multiply(bignumber(response.data.data.simulation.calculationDetails.commissionBackService), 100);
        const index = response.data.data.simulation.calculationResults.findIndex(r => r.calculationResultStatusId > 1);
        if (index >= 0) {
          commit('setAcceptedResult', response.data.data.simulation.calculationResults[index].calculationType.calculationTypeId.toString());
          commit('setChoicesDisabled', true);
          if (response.data.data.simulation.calculationResults[index].calculationType.calculationTypeId === 4) {
            commit('setAcceptedPremiumPension', response.data.data.simulation.calculationResults[index].annualPremiumPensionAndDeath);
            commit('setAcceptedBackservice', response.data.data.simulation.calculationResults[index].backservice);
          } else {
            commit('setAcceptedPremiumPension', 0);
            commit('setAcceptedBackservice', 0);
          }
        } else {
          commit('setAcceptedResult', '');
          commit('setChoicesDisabled', false);
          commit('setAcceptedPremiumPension', 0);
          commit('setAcceptedBackservice', 0);
        }


        commit('setSimulation', response.data.data.simulation);
      } else {
        dispatch('general/setError', true, { root: true });
      }
      commit('setLoading', false);
    }
  },
  // eslint-disable-next-line no-shadow
  async setCalculationState({ commit }, calculationState) {
    commit('setCalculationState', calculationState);
  },
  // eslint-disable-next-line no-shadow
  async updateSimulation({ commit, state, dispatch }) {
    if (state.simulation.simulationId) {
      const response = await simulationRepo
        .updateSimulation(state.simulation.simulationId, state.explanation);
      if (response && response.status === 200 && !response.data.errors) {
        commit('setContractIdSaved', response.data.data.updateSimulation.contractId);
        commit('setCalculationSaved', true);
      } else {
        dispatch('general/setError', true, { root: true });
      }
    }
  },
  // eslint-disable-next-line no-shadow
  async acceptSimulation({ state, dispatch }) {
    if (state.simulation.simulationId && state.acceptedResult) {
      const response = await simulationRepo
        .acceptSimulation(parseInt(state.simulation.simulationId, 10), parseInt(state.acceptedResult, 10), state.acceptedPremiumPension, state.acceptedBackservice, state.comment);
      if (response && response.status === 200 && !response.data.errors) {
        dispatch('detail/setDefaultState', true, { root: true });
        dispatch('getSimulationDetails', state.simulation.simulationId);
      } else {
        dispatch('general/setError', true, { root: true });
      }
    }
  },
  // eslint-disable-next-line no-shadow
  async processSimulation({ state, dispatch }) {
    if (state.simulation.simulationId && state.acceptedResult) {
      const response = await simulationRepo
        .processSimulation(parseInt(state.simulation.simulationId, 10), parseInt(state.acceptedResult, 10));
      if (response && response.status === 200 && !response.data.errors) {
        dispatch('detail/setDefaultState', true, { root: true });
        dispatch('getSimulationDetails', state.simulation.simulationId);
      } else {
        dispatch('general/setError', true, { root: true });
      }
    }
  },
  // eslint-disable-next-line no-shadow
  async saveAnswers({ commit, state, dispatch }) {
    if (state.answers !== [] && state.simulationId && state.professionId) {
      const response = await simulationRepo
        .saveSimulationAnswers(state.answers, state.simulationId, state.professionId);
      if (response && response.status === 200 && !response.data.errors) {
        commit('setSimulationId', response.data.data.createSimulationAnswers.simulationId);
        commit('setCalculationState', response.data.data.createSimulationAnswers.state);
        if (state.calculationState === 'moreInfoRequired') {
          commit('setQuestions', response.data.data.createSimulationAnswers.questions);
          commit('setProfessionId', response.data.data.createSimulationAnswers.professionId);
          commit('setAnswers', response.data.data.createSimulationAnswers.questions);
        } else {
          dispatch('getSimulationDetails');
        }
      } else {
        dispatch('general/setError', true, { root: true });
      }
    }
  },
  async setDefaultState({ commit, rootState }) {
    commit('setDefaultState',
      {
        maritalState: rootState.detail.customer.maritalState,
        commissionBackService: rootState.detail.primaryContract.commissionBackService,
        commissionPremium: rootState.detail.primaryContract.commissionPremiumPensionAndDeath,
      });
  },
};


export default {
  state,
  mutations,
  getters,
  actions,
};
